import React from 'react';
import { RiskAttribution as RiskAttributionData } from '../../services/portfolio';
import InfoTooltip from '../ui/InfoTooltip';

// Component (Euler) risk-contribution decomposition: each holding's share of
// total portfolio volatility, shown against its capital weight so the reader
// can see which positions punch above their weight in risk terms.

const NAVY = '#000537';
const GREY = '#a1a1aa';

const pct1 = (v: number) => `${(v * 100).toFixed(1)}%`;
const pct0 = (v: number) => `${(v * 100).toFixed(0)}%`;

// Inline monospace formula chip — matches the other analytics cards' info boxes.
const f = (s: string) => (
  <code style={{ fontFamily: 'monospace', fontSize: '0.68rem', background: '#f4f4f5', borderRadius: '3px', padding: '1px 4px', color: '#18181b' }}>{s}</code>
);

const infoBox = (
  <div style={{ fontSize: '0.72rem', color: '#52525b', lineHeight: 1.55 }}>
    <div style={{ fontWeight: 600, color: NAVY, marginBottom: 4 }}>Risk attribution</div>
    Each holding&apos;s share of total portfolio volatility, via the Euler decomposition
    {' '}{f('cᵢ = wᵢ·(Σw)ᵢ / σ_p')} — contributions sum to the portfolio&apos;s vol.
    A position drives more risk than its weight when it is both heavy and highly
    correlated with the rest of the book; a low-correlation name drives less.
    <div style={{ marginTop: 6 }}>
      <b>Risk / weight</b> &gt; 1 means it carries more of the risk than the capital.
      The <b>diversification ratio</b> is the weighted-average standalone vol divided
      by the (lower) portfolio vol — how much the blend cancels out.
    </div>
  </div>
);

const RiskAttribution: React.FC<{ data: RiskAttributionData }> = ({ data }) => {
  const positions = data.positions || [];
  if (positions.length === 0) return null;

  // Fold the long tail (<1.5% of risk) into one row to keep the chart legible.
  const MIN = 0.015;
  const shown = positions.filter((p) => p.percent_of_risk >= MIN);
  const tail = positions.filter((p) => p.percent_of_risk < MIN);
  if (tail.length > 1) {
    shown.push({
      identifier: '__other__',
      name: `Other (${tail.length})`,
      weight: tail.reduce((s, p) => s + p.weight, 0),
      volatility: 0,
      correlation_to_portfolio: 0,
      marginal_contribution: 0,
      risk_contribution: tail.reduce((s, p) => s + p.risk_contribution, 0),
      percent_of_risk: tail.reduce((s, p) => s + p.percent_of_risk, 0),
      risk_to_weight: null,
    });
  }

  // Scale bars to the largest risk share so the biggest contributor fills the track.
  const max = Math.max(...shown.map((p) => Math.max(p.percent_of_risk, p.weight)), 0.0001);
  const divPct = data.diversification_ratio ? 1 - 1 / data.diversification_ratio : null;

  return (
    <div className="card card-forest" style={{ marginTop: 12 }}>
      <div className="card-body p-3 p-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-semibold" style={{ fontSize: '0.95rem', color: NAVY }}>
            What&apos;s driving your risk
          </span>
          <span className="d-flex align-items-center" style={{ fontSize: '0.75rem', color: GREY }}>
            {data.window_years}Y
            <InfoTooltip text={infoBox} width={420} placement="bottom" />
          </span>
        </div>

        <div style={{ fontSize: '0.74rem', color: '#52525b', marginBottom: 14 }}>
          Portfolio vol <b style={{ color: NAVY }}>{pct1(data.portfolio_volatility)}</b>
          {' · '}weighted-avg standalone {pct1(data.weighted_avg_volatility)}
          {divPct != null && (
            <> · diversification cuts <b style={{ color: '#15803d' }}>~{pct0(divPct)}</b></>
          )}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, fontSize: '0.7rem', color: GREY, marginBottom: 8 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: NAVY, borderRadius: 2, verticalAlign: 'middle', marginRight: 5 }} />Share of risk</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#d4d4d8', borderRadius: 2, verticalAlign: 'middle', marginRight: 5 }} />Weight</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {shown.map((p) => {
            const rw = p.risk_to_weight;
            const flag = rw != null && (rw >= 1.15 || rw <= 0.85);
            return (
              <div key={p.identifier} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 92px', gap: 10, alignItems: 'center' }}>
                <div style={{ fontSize: '0.78rem', color: '#27272a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name || p.identifier}>
                  {p.name || p.identifier}
                </div>

                {/* Paired bars: risk share (navy) over weight (grey) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ height: 7, background: '#f4f4f5', borderRadius: 4 }}>
                    <div style={{ width: `${(p.percent_of_risk / max) * 100}%`, height: '100%', background: NAVY, borderRadius: 4 }} />
                  </div>
                  <div style={{ height: 7, background: '#f4f4f5', borderRadius: 4 }}>
                    <div style={{ width: `${(p.weight / max) * 100}%`, height: '100%', background: '#d4d4d8', borderRadius: 4 }} />
                  </div>
                </div>

                <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY }}>{pct0(p.percent_of_risk)}</span>
                  {flag && (
                    <span style={{
                      fontSize: '0.66rem', marginLeft: 5, padding: '1px 5px', borderRadius: 4,
                      color: rw! > 1 ? '#9a3412' : '#3f6212',
                      background: rw! > 1 ? '#ffedd5' : '#ecfccb',
                    }}>
                      {rw!.toFixed(1)}×
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RiskAttribution;
